// The distribution a forecast would have IF a hovered trigger happens, for the hover preview on
// the timeline's distribution block. Pure: no React, no DOM.
import { eventKey } from "../forecast/eventIdentity.js";
import { leadingRow } from "../forecast/multiway.js";
import { triggerOutcomeShiftsOf } from "../forecast/reportAnalysis.js";
import { ifOccursProbability, settledOutcome } from "./signposts.js";

const isProb = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 1;

/** Longest trigger title the caption quotes before it is cut on a word. */
export const CAPTION_TITLE_MAX = 44;

/**
 * The trigger a hover key names in one row of the timeline, or null. Trigger marks set `hov` to
 * `<rowId>::<eventKey>` and collapsed dots to `dot::<rowId>::<eventKey>`; every other hover
 * (`res-…`, `rbr…`) names no row here.
 *
 * @param {string|null} hov the app's hover key
 * @param {string} rowId the row the lane drew its triggers in ("main" for the primary lane)
 * @param {object[]} events the triggers that row drew
 */
export function hoveredTrigger(hov, rowId, events) {
  if (typeof hov !== "string") return null;
  const prefix = [`${rowId}::`, `dot::${rowId}::`].find((p) => hov.startsWith(p));
  if (!prefix) return null;
  const key = hov.slice(prefix.length);
  return (Array.isArray(events) ? events : []).find((ev) => ev && eventKey(ev) === key) ?? null;
}

/** "Vance succeeds to the presidency before…": cut on a word, at most `max` characters. */
export function truncateTitle(title, max = CAPTION_TITLE_MAX) {
  const t = String(title ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.5 ? cut.slice(0, space) : cut).replace(/[\s,;:.–—-]+$/, "")}…`;
}

/** The yes/no lead: the side of 50%, none at exactly 50% (as the leader alerts read it). */
function binaryLeadIndex(pYes) {
  if (!isProb(pYes) || pYes === 0.5) return null;
  return pYes > 0.5 ? 0 : 1;
}

/** Index into `buckets` of the row `leadingRow` picks when each bucket weighs `values[i]`. */
function leadIndex(buckets, values) {
  const rows = buckets.map((b, i) => ({ ...b, p: values[i], _i: i }));
  return leadingRow(rows)?._i ?? null;
}

/** P(each bucket | the trigger happens), aligned with `buckets`, or null when any is missing. */
function conditionalValues(ev, buckets, kind) {
  if (kind === "binary") {
    const pYes = ifOccursProbability(ev);
    return isProb(pYes) ? [pYes, 1 - pYes] : null;
  }
  // The report's trigger ticker already pairs a_pmf (if it happens) with the buckets — object
  // keys by canonical label, array entries by outcome slot — so the preview reads it there.
  const shifts = triggerOutcomeShiftsOf(ev, buckets, { max: Infinity, minSwing: 0 });
  if (!shifts) return null;
  const byLabel = new Map(shifts.rows.map((r) => [r.label, r.ifYes]));
  // The catch-all's key can be spelled differently from its bucket ("other" / "Other"); a
  // single unmatched key is then that slot.
  const known = new Set(buckets.map((b) => b.label));
  const stray = shifts.rows.filter((r) => !known.has(r.label));
  const values = buckets.map((b) =>
    byLabel.has(b.label)
      ? byLabel.get(b.label)
      : b.isOther === true && stray.length === 1
        ? stray[0].ifYes
        : undefined,
  );
  if (!values.every(isProb)) return null;
  // A conditional that does not sum to one would draw bars that disagree with each other.
  const sum = values.reduce((s, v) => s + v, 0);
  return Math.abs(sum - 1) <= 0.02 ? values : null;
}

/**
 * The forecast if `ev` happens, drawn over `dist` (the distribution the block shows), or null
 * when there is nothing to preview: no trigger, a trigger already settled, a question already
 * settled, or a trigger without conditional data.
 *
 * @param {object|null} ev the hovered trigger
 * @param {{buckets: Array<{label: string, p: number}>, kind: string, resolvedIndex?: number|null, badge?: string|null}|null} dist
 * @param {{resolutions?: object|null}} [opts] graded trigger outcomes keyed by trigger id
 * @returns {null | {
 *   caption: string,          // "If “…” happens"
 *   values: number[],         // P(bucket | trigger happens), aligned with dist.buckets
 *   leadBefore: number|null,  // index into dist.buckets of today's leader
 *   leadAfter: number|null,   // … and of the leader if it happens
 *   leaderChanged: boolean,
 * }}
 */
export function hoverPreviewOf(ev, dist, { resolutions = null } = {}) {
  if (!ev || !dist || !Array.isArray(dist.buckets) || dist.buckets.length < 2) return null;
  if (dist.resolvedIndex != null || dist.badge) return null;
  if (ev.isPast || settledOutcome(ev, resolutions) != null) return null;
  const { buckets, kind } = dist;
  const values = conditionalValues(ev, buckets, kind);
  if (!values) return null;
  const binary = kind === "binary";
  const leadBefore = binary
    ? binaryLeadIndex(buckets[0]?.p)
    : leadIndex(
        buckets,
        buckets.map((b) => b.p || 0),
      );
  const leadAfter = binary ? binaryLeadIndex(values[0]) : leadIndex(buckets, values);
  const title = truncateTitle(ev.title);
  return {
    caption: title ? `If “${title}” happens` : "If this trigger happens",
    values,
    leadBefore,
    leadAfter,
    leaderChanged: leadBefore != null && leadAfter != null && leadBefore !== leadAfter,
  };
}

/** A move in whole points as "+13" / "−4", or "" when it rounds to nothing. */
export function fmtDeltaPts(from, to) {
  if (!isProb(from) || !isProb(to)) return "";
  const pts = Math.round((to - from) * 100);
  if (pts === 0) return "";
  return pts > 0 ? `+${pts}` : `−${Math.abs(pts)}`;
}
